<script lang="ts">
import { defineComponent, reactive } from 'vue'
import ContentsCard from '@/components/contents/ContentsCard.vue'
import LayoutStoryCardsCols from '@/components/contents/LayoutStoryCardsCols.vue'
import stateInitializer from '@/models/stateInitializer'

export default defineComponent({
  name: 'ThemeStoriesMain',

  components: {
    ContentsCard,
    LayoutStoryCardsCols,
  },

  setup() {
    const IMAGE_OPTIONS = {
      width: {
        pc: 202,
        sp: 214,
      },
      height: {
        pc: 88,
        sp: 80,
      },
    } as const

    interface State {
      heroStories: StoryItem[],
      regularStories: StoryItem[],
    }

    const state: State = reactive({
      heroStories: [],
      regularStories: [],
    })

    stateInitializer(state, 'main')

    const topItem = (item: StoryItem) => {
      return item.report?.items?.[0] || item.article?.items?.[0]
    }

    return {
      // constants
      IMAGE_OPTIONS,

      // data
      state,

      // methods
      topItem,
    }
  },
})
</script>

<template>
  <section class="section">
    <section class="section">
      <div
        v-if="state.heroStories.length"
        class="o-list__heroStories">
        <ul class="o-list__heroStories--items">
          <li
            v-for="(story, index) in state.heroStories"
            :key="story.id"
            class="o-list__heroStories--item">
            <a
              class="l-unit__heroStoryCard"
              :href="story.to">
              <div
                v-if="story.thumbImageUrl"
                class="l-unit__heroStoryCard--image">
                <img
                  class="img"
                  :src="story.thumbImageUrl"
                  :loading="index === 0 ? undefined : 'lazy'"
                  :fetchpriority="index === 0 ? 'high' : undefined"
                  :alt="story.title">
              <!-- /.l-unit__heroStoryCard image --></div>

              <div
                v-if="topItem(story) && topItem(story)?.to"
                class="l-unit__heroStoryCard--description">
                <ContentsCard
                  :item="topItem(story)"
                  :has-hero="true"
                  :horizontal="{
                    pc: true,
                    sp: true,
                  }"
                  :heading-options="{
                    maxLine: 2,
                  }"
                  :image-options="{
                    width: {
                      pc: 72,
                      sp: 72,
                    },
                    height: {
                      pc: 72,
                      sp: 72,
                    },
                  }" />
              <!-- /.l-unit__heroStoryCard description --></div>
            <!-- /.l-unit__heroStoryCard --></a>
          </li>
        </ul>
      <!-- /.o-list__heroStories --></div>
    </section>

    <section class="section">
      <LayoutStoryCardsCols
        v-if="state.regularStories.length"
        :items="state.regularStories"
        :cols="{ pc: 3, sp: 2 }"
        :heading-options="{ maxLine: 2 }"
        :image-options="IMAGE_OPTIONS"
        has-items-total />
    </section>
  </section>
</template>

<style lang="sass" scoped>
// layout
.l-unit__heroStoryCard
  $root: &
  $image-width: 308
  $image-height: 136
  &[href]
    text-decoration: none

    +mouseover
      #{$root}--image
        opacity: 0.5

      &:has(#{$root}--description > *[href]:hover)
        #{$root}--image
          opacity: 1

  & &--image
    position: relative
    +transition

    &::before
      content: ''
      width: 0
      display: block
      padding-top: rate(math.div($image-height, $image-width))

    > .img
      height: 100%
      display: block
      object-fit: cover
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)

  & &--description
    position: relative
    margin-top: 1em

    &:first-child
      margin-top: 0

  +breakpoint
    $image-width: 214
    $image-height: 80

    #{$root}--image
      &::before
        padding-top: rate(math.div($image-height, $image-width))

// object
.o-list__heroStories
  $root: &

  & &--items
    display: grid
    grid-template-columns: repeat(2, 1fr)
    gap: 1.5em

  +breakpoint
    #{$root}--items
      grid-template-columns: 1fr
</style>
